// ---------------------------------------------------------------------------
// Fetch Presets -- Loads saved design system configurations from Supabase
//
// Queries the `design_system_presets` table in the `cogo` schema.
// Error contract: PGRST106 (schema not exposed) and PGRST205 (table not found)
// return { presets: [], error: null }. Other errors return { presets: [], error: message }.
// See FIND-002 in .planning/phases/397-tier1-e2e-ux-audit-manual/397-UX-FINDINGS.md
// ---------------------------------------------------------------------------

import { supabase } from '@/lib/supabase'
import { handleCogoSchemaResult } from '@/lib/supabase-helpers'
import type { DesignSystemState } from './design-system-options'

export interface DesignSystemPreset {
  id: string
  name: string
  description: string
  tokens: DesignSystemState
  createdAt: string
}

export interface FetchPresetsResult {
  presets: DesignSystemPreset[]
  error?: string
}

export async function fetchDesignSystemPresets(
  userId: string,
): Promise<FetchPresetsResult> {
  try {
    const result = await supabase
      .schema('cogo')
      .from('design_system_presets')
      .select('id, name, description, tokens, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    const { data, error } = handleCogoSchemaResult(result, [] as any[])

    if (error) {
      return { presets: [], error }
    }

    const presets: DesignSystemPreset[] = (data ?? []).map((row: any) => ({
      id: row.id,
      name: row.name,
      description: row.description,
      tokens: row.tokens,
      createdAt: row.created_at,
    }))

    return { presets }
  } catch (e) {
    return {
      presets: [],
      error: e instanceof Error ? e.message : 'Unexpected error fetching presets',
    }
  }
}
